import { NgClass, NgFor, NgIf } from '@angular/common';
import { Component, Input, OnInit, inject, signal } from '@angular/core';
import { RouterLink } from '@angular/router';
import { TenantUserRoles } from '@buspro/shared/common';
import { TenantRolesService } from '@buspro/web/core';
import { NzTooltipDirective } from 'ng-zorro-antd/tooltip';

export interface QuickAction {
  label: string;
  icon: string;
  iconType?: 'fe' | 'fa';
  route?: string;
  action?: () => void;
  roles?: TenantUserRoles[];
  badge?: number;
}

@Component({
  selector: 'tenant-sidebar-quick-actions',
  standalone: true,
  imports: [NgClass, NgFor, NgIf, RouterLink, NzTooltipDirective],
  template: `
    <div class="quick-actions" [class.collapsed]="isCollapsed">
      <div class="quick-actions-header" *ngIf="!isCollapsed">
        <span class="header-label">Quick Actions</span>
      </div>
      
      <div class="quick-actions-list" [class.collapsed]="isCollapsed">
        <button
          *ngFor="let action of visibleActions()"
          [routerLink]="action.route"
          (click)="action.action?.()"
          [nzTooltipTitle]="isCollapsed ? action.label : null"
          [nzTooltipPlacement]="'right'"
          nz-tooltip
          class="quick-action-item"
          [class.collapsed]="isCollapsed"
        >
          <span
            [ngClass]="getActionIconClasses(action)"
            class="action-icon"
          ></span>
          <span class="action-label" *ngIf="!isCollapsed">{{ action.label }}</span>
          <span class="action-badge" *ngIf="!isCollapsed && action.badge !== undefined && action.badge > 0">
            {{ action.badge }}
          </span>
        </button>
      </div>
    </div>
  `,
  styles: [
    `
      .quick-actions {
        padding: 12px 8px;
        border-bottom: 1px solid var(--sidebar-border);
        margin-bottom: 8px;
        
        .quick-actions-header {
          margin-bottom: 8px;
          
          .header-label {
            font-size: 11px;
            font-weight: 600;
            text-transform: uppercase;
            letter-spacing: 0.5px;
            color: var(--sidebar-text-secondary);
            padding: 0 12px;
          }
        }
        
        .quick-actions-list {
          display: flex;
          flex-direction: column;
          gap: 4px;
          
          &.collapsed {
            align-items: center;
          }
        }
        
        .quick-action-item {
          display: flex;
          align-items: center;
          padding: 10px 12px;
          background: var(--accent-primary);
          color: white;
          border: none;
          border-radius: 8px;
          cursor: pointer;
          transition: all var(--transition-base);
          text-decoration: none;
          font-size: 14px;
          font-weight: 500;
          
          &:hover {
            background: var(--accent-primary-hover);
            transform: translateY(-1px);
            box-shadow: var(--shadow-md);
          }
          
          &:active {
            transform: translateY(0);
          }
          
          .action-icon {
            font-size: 16px;
            width: 20px;
            height: 20px;
            display: flex;
            align-items: center;
            justify-content: center;
            flex-shrink: 0;
          }
          
          .action-label {
            margin-left: 10px;
            flex: 1;
            text-align: left;
          }
          
          .action-badge {
            margin-left: auto;
            background-color: rgba(255, 255, 255, 0.3);
            color: white;
            font-size: 11px;
            font-weight: 600;
            padding: 2px 6px;
            border-radius: 10px;
            min-width: 18px;
            text-align: center;
          }
          
          &.collapsed {
            justify-content: center;
            padding: 10px;
            width: 40px;
            height: 40px;
            
            .action-label,
            .action-badge {
              display: none;
            }
          }
        }
        
        &.collapsed {
          padding: 8px 4px;
          border-bottom: 1px solid var(--sidebar-border);
        }
      }
    `,
  ],
})
export class SidebarQuickActionsComponent implements OnInit {
  @Input() actions: QuickAction[] = [];
  @Input() isCollapsed = false;

  private roleService = inject(TenantRolesService);
  visibleActions = signal<QuickAction[]>([]);

  async ngOnInit() {
    await this.filterActions();
  }

  async filterActions() {
    const filtered: QuickAction[] = [];

    for (const action of this.actions) {
      if (!action.roles || action.roles.length === 0) {
        filtered.push(action);
        continue;
      }

      // Check if user has any of the required roles
      let hasAccess = false;
      for (const role of action.roles) {
        if (await this.roleService.hasRole(role)) {
          hasAccess = true;
          break;
        }
      }

      if (hasAccess) {
        filtered.push(action);
      }
    }

    // Limit to top 5 most relevant actions to avoid clutter
    // Prioritize actions with routes over actions without
    const sorted = filtered.sort((a, b) => {
      if (a.route && !b.route) return -1;
      if (!a.route && b.route) return 1;
      return 0;
    });

    this.visibleActions.set(sorted.slice(0, 5));
  }

  getActionIconClasses(action: QuickAction): { [key: string]: boolean } {
    const classes: { [key: string]: boolean } = {};
    if (action.iconType !== 'fa') {
      classes['fe'] = true;
    } else {
      classes['fa'] = true;
    }
    classes[action.icon] = true;
    return classes;
  }
}
